import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "./Trade.css";
import { splitDateTime, formatNumberWithComma } from "../../../Common/Helpers";
import CoinInformation from "../../../Common/CoinInformation.json";
import { fetchMarketData } from "../../../Components/MarketData";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useModalContext } from "../../../Context/ModalContext";
import { useSelector } from "react-redux";
import { WebSocketContext } from "../../../Context/WebSocketContext";

export const Portfolio = ({ tradeCompleted, setTradeCompleted, coin }) => {
  const { selectedNumberFormat, selectedFiatEstimation } = useModalContext();
  const { userOpenOrders, userBalance } = useContext(WebSocketContext);

  const token = localStorage.getItem("token");

  // ✅ Get all global data from Redux store (SAME AS OVERVIEW)
  const labels = useSelector((state) => state.labels.data);
  const priceState = useSelector((state) => state.price);
  const crypto_price = priceState?.crypto_price || { data: { rateList: [] } };
  const fiat_price = priceState?.fiat_price || { data: { rateList: [] } };

  // Memoized tabs to prevent unnecessary re-renders
  const tabs = useMemo(
    () => [
      { id: 0, name: labels?.exchange?.openOrders },
      { id: 1, name: labels?.exchange?.orderHistory },
      { id: 2, name: labels?.exchange?.portfolio },
    ],
    [labels]
  );

  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [tradeHistoryData, setTradeHistoryData] = useState([]);
  const [portfolioData, setPortfolioData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState({ key: "date", ascending: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [hideBalances, setHideBalances] = useState(false);
  const [hideLowBalances, setHideLowBalances] = useState(false);
  const [btcValue, setBtcValue] = useState();
  const [USDTValue, setUSDTValue] = useState();
  const [totalInUsdt, setTotalInUsdt] = useState();
  const [totalInBtc, setTotalInBtc] = useState();
  const [deletedOrderId, setDeletedOrderId] = useState();
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedCurrencyRate, setSelectedCurrencyRate] = useState();
  const [completeUserBalance, setCompleteUserBalance] = useState({});

  // ✅ Refs to track initial load and current coin pair
  const hasInitiallyLoaded = useRef(false);
  const currentCoinPair = useRef(null);
  const previousOpenOrdersCount = useRef(0);

  const handleTabClick = useCallback((tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((key) => {
    setSortBy((prevSortBy) => ({
      key,
      ascending: prevSortBy.key === key ? !prevSortBy.ascending : true,
    }));
  }, []);

  const toggleRow = useCallback((id) => {
    setExpandedRows((prevExpandedRows) => ({
      ...prevExpandedRows,
      [id]: !prevExpandedRows[id],
    }));
  }, []);

  const fetchMarketDataFunction = async () => {
    try {
      let latestFiatData = await fetchMarketData();

      const filteredArray = latestFiatData.filter(
        (item) => item.currency === "BTC"
      );

      const btcRate = filteredArray[0]?.rate;
      setBtcValue(btcRate);

      const filteredArray2 = latestFiatData.filter(
        (item) => item.currency === "USDT"
      );

      const usdtRate = filteredArray2[0]?.rate;
      setUSDTValue(usdtRate);
    } catch (error) {}
  };

  // ✅ SAME LOGIC AS OVERVIEW.JS
  const processBalance = useCallback(() => {
    const cryptoPrices = crypto_price?.data?.rateList;

    if (
      !userBalance ||
      !cryptoPrices?.length ||
      !btcValue ||
      !USDTValue ||
      btcValue === 0 ||
      USDTValue === 0
    ) {
      return;
    }

    let tmpTotalInUsdt = 0;
    let tmpTotalInBtc = 0;

    try {
      // Convert userBalance object to array format
      const balanceArray = Object.entries(userBalance).map(
        ([currency, data]) => ({
          currency,
          balance: data.balance,
          balanceInTrade: data.balance_in_trade,
        })
      );

      const { coinListAll } = getBalanceCoinData(balanceArray);
      const sortedCoinListAll = coinListAll.sort(
        (a, b) => b.balance - a.balance
      );

      // Calculate prices for each coin
      sortedCoinListAll.forEach((item2) => {
        const cryptoPrice = cryptoPrices?.find(
          (crypto) => crypto.currency === item2.currency
        );

        if (item2.currency === "USDT") {
          const usdtRate = USDTValue || 1;
          item2.balanceInUsdt = parseFloat(item2.balance) * usdtRate;
          item2.balanceInBtc =
            btcValue > 0 ? item2.balanceInUsdt / btcValue : 0;
          item2.balanceInTradeInUsdt =
            parseFloat(item2.balanceInTrade || 0) * usdtRate;
          item2.balanceInTradeInBtc =
            btcValue > 0 ? item2.balanceInTradeInUsdt / btcValue : 0;
          item2.total =
            parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
          item2.totalInUsdt = item2.total * usdtRate;
          item2.totalInBtc = btcValue > 0 ? item2.totalInUsdt / btcValue : 0;

          if (item2.balanceInUsdt > 0) tmpTotalInUsdt += item2.balanceInUsdt;
          if (item2.balanceInBtc > 0) tmpTotalInBtc += item2.balanceInBtc;
        } else if (item2.currency === "BTC") {
          item2.balanceInBtc = parseFloat(item2.balance);
          item2.balanceInUsdt = item2.balanceInBtc * btcValue;
          item2.balanceInTradeInBtc = parseFloat(item2.balanceInTrade || 0);
          item2.balanceInTradeInUsdt = item2.balanceInTradeInBtc * btcValue;
          item2.total =
            parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
          item2.totalInBtc = item2.total;
          item2.totalInUsdt = item2.total * btcValue;

          if (item2.balanceInUsdt > 0) tmpTotalInUsdt += item2.balanceInUsdt;
          if (item2.balanceInBtc > 0) tmpTotalInBtc += item2.balanceInBtc;
        } else if (cryptoPrice) {
          const usdtValue = parseFloat(item2.balance) * cryptoPrice.rate;
          item2.balanceInUsdt = usdtValue;
          item2.balanceInBtc = btcValue > 0 ? usdtValue / btcValue : 0;

          const tradeUsdtValue =
            parseFloat(item2.balanceInTrade || 0) * cryptoPrice.rate;
          item2.balanceInTradeInUsdt = tradeUsdtValue;
          item2.balanceInTradeInBtc =
            btcValue > 0 ? tradeUsdtValue / btcValue : 0;

          item2.total =
            parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
          item2.totalInUsdt = item2.total * cryptoPrice.rate;
          item2.totalInBtc = btcValue > 0 ? item2.totalInUsdt / btcValue : 0;

          if (item2.balanceInUsdt > 0) tmpTotalInUsdt += item2.balanceInUsdt;
          if (item2.balanceInBtc > 0) tmpTotalInBtc += item2.balanceInBtc;
        } else {
          item2.balanceInUsdt = 0;
          item2.balanceInBtc = 0;
          item2.balanceInTradeInUsdt = 0;
          item2.balanceInTradeInBtc = 0;
          item2.total =
            parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
          item2.totalInUsdt = 0;
          item2.totalInBtc = 0;
        }
      });

      setTotalInUsdt(tmpTotalInUsdt);
      setTotalInBtc(tmpTotalInBtc);
      setPortfolioData(sortedCoinListAll);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [btcValue, USDTValue, crypto_price, userBalance]);

  // ✅ SAME processBalanceWithData AS OVERVIEW
  const processBalanceWithData = useCallback(
    (balanceData) => {
      const cryptoPrices = crypto_price?.data?.rateList;

      if (
        !balanceData ||
        !cryptoPrices?.length ||
        !btcValue ||
        !USDTValue ||
        btcValue === 0 ||
        USDTValue === 0
      ) {
        return;
      }

      let tmpTotalInUsdt = 0;
      let tmpTotalInBtc = 0;

      try {
        const balanceArray = Object.entries(balanceData).map(
          ([currency, data]) => ({
            currency,
            balance: data.balance,
            balanceInTrade: data.balance_in_trade,
          })
        );

        const { coinListAll } = getBalanceCoinData(balanceArray);
        const sortedCoinListAll = coinListAll.sort(
          (a, b) => b.balance - a.balance
        );

        sortedCoinListAll.forEach((item2) => {
          const cryptoPrice = cryptoPrices?.find(
            (crypto) => crypto.currency === item2.currency
          );

          if (item2.currency === "USDT") {
            item2.balanceInUsdt = parseFloat(item2.balance) * USDTValue;
            item2.balanceInBtc =
              btcValue > 0 ? item2.balanceInUsdt / btcValue : 0;
            item2.balanceInTradeInUsdt =
              parseFloat(item2.balanceInTrade || 0) * USDTValue;
            item2.balanceInTradeInBtc =
              btcValue > 0 ? item2.balanceInTradeInUsdt / btcValue : 0;
            item2.total =
              parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
            item2.totalInUsdt = item2.total * USDTValue;
            item2.totalInBtc = btcValue > 0 ? item2.totalInUsdt / btcValue : 0;

            if (item2.balanceInUsdt > 0) tmpTotalInUsdt += item2.balanceInUsdt;
            if (item2.balanceInBtc > 0) tmpTotalInBtc += item2.balanceInBtc;
          } else if (item2.currency === "BTC") {
            item2.balanceInBtc = parseFloat(item2.balance);
            item2.balanceInUsdt = item2.balanceInBtc * btcValue;
            item2.balanceInTradeInBtc = parseFloat(item2.balanceInTrade || 0);
            item2.balanceInTradeInUsdt = item2.balanceInTradeInBtc * btcValue;
            item2.total =
              parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
            item2.totalInBtc = item2.total;
            item2.totalInUsdt = item2.total * btcValue;

            if (item2.balanceInUsdt > 0) tmpTotalInUsdt += item2.balanceInUsdt;
            if (item2.balanceInBtc > 0) tmpTotalInBtc += item2.balanceInBtc;
          } else if (cryptoPrice) {
            const usdtValue = parseFloat(item2.balance) * cryptoPrice.rate;
            item2.balanceInUsdt = usdtValue;
            item2.balanceInBtc = btcValue > 0 ? usdtValue / btcValue : 0;

            const tradeUsdtValue =
              parseFloat(item2.balanceInTrade || 0) * cryptoPrice.rate;
            item2.balanceInTradeInUsdt = tradeUsdtValue;
            item2.balanceInTradeInBtc =
              btcValue > 0 ? tradeUsdtValue / btcValue : 0;

            item2.total =
              parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
            item2.totalInUsdt = item2.total * cryptoPrice.rate;
            item2.totalInBtc = btcValue > 0 ? item2.totalInUsdt / btcValue : 0;

            if (item2.balanceInUsdt > 0) tmpTotalInUsdt += item2.balanceInUsdt;
            if (item2.balanceInBtc > 0) tmpTotalInBtc += item2.balanceInBtc;
          } else {
            item2.balanceInUsdt = 0;
            item2.balanceInBtc = 0;
            item2.balanceInTradeInUsdt = 0;
            item2.balanceInTradeInBtc = 0;
            item2.total =
              parseFloat(item2.balance) - parseFloat(item2.balanceInTrade || 0);
            item2.totalInUsdt = 0;
            item2.totalInBtc = 0;
          }
        });

        setTotalInUsdt(tmpTotalInUsdt);
        setTotalInBtc(tmpTotalInBtc);
        setPortfolioData(sortedCoinListAll);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    },
    [btcValue, USDTValue, crypto_price]
  );

  const getBalanceCoinData = (data) => {
    const serviceData = data ? [...data] : [];
    const coinInformation = CoinInformation;
    let coinListAll = [];

    for (let i = 0; i < serviceData.length; i++) {
      const coinName = serviceData[i].currency;

      if (coinInformation[coinName]?.ShortName) {
        serviceData[i].ShortName = coinInformation[coinName].ShortName;
        serviceData[i].LongName = coinInformation[coinName].LongName;
        coinListAll.push(serviceData[i]);
      } else {
        serviceData[i].ShortName = serviceData[i].currency;
        serviceData[i].LongName = serviceData[i].currency;
        coinListAll.push(serviceData[i]);
      }
    }
    return { coinListAll };
  };

  const fetchTradeHistory = async () => {
    if (!token || !coin?.base || !coin?.quote) return;

    setIsLoading(true);
    const largeCount = 1000;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_REST_URL}/api/TradeHistory?side=ALL&pair=ALL&page=1&count=${largeCount}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const latestData = response?.data?.data?.rows;
      const filteredData = latestData.filter(
        (item) => item.trade === coin?.base && item.market === coin.quote
      );

      setTradeHistoryData(filteredData);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const cancelOrder = async (id, side, pair) => {
    const requestBody = { side: side, pair: pair, orderId: id };

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_REST_URL}/api/CancelOrder`,
        requestBody,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response?.data?.status != "Error") {
        const message = labels?.messages?.orderCancelled;
        toast.success(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        setTradeCompleted(!tradeCompleted);
      } else {
        const message = response.data.message;
        toast.error(message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      }
    } catch (error) {}
  };

  // ✅ Initial data fetch on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        await fetchMarketDataFunction();
      } catch (error) {
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // ✅ Update selected currency rate from Redux store
  useEffect(() => {
    if (selectedFiatEstimation?.name && fiat_price?.data?.rateList) {
      const rateList = fiat_price.data.rateList;
      const selectedCurrency = rateList.find(
        (item) => item.currency === selectedFiatEstimation?.name
      );

      if (selectedCurrency) {
        setSelectedCurrencyRate(selectedCurrency.rate);
      }
    }
  }, [selectedFiatEstimation?.name, fiat_price]);

  useEffect(() => {
    const cryptoPrices = crypto_price?.data?.rateList;

    const btcPrice = cryptoPrices?.find((item) => item.currency === "BTC");
    const usdtPrice = cryptoPrices?.find((item) => item.currency === "USDT");

    const btcRate = btcPrice?.rate || 1;
    const usdtRate = usdtPrice?.rate || 1;

    if (!userBalance || !cryptoPrices?.length || !btcRate || !usdtRate) {
      return;
    }

    if (!btcValue) setBtcValue(btcRate);
    if (!USDTValue) setUSDTValue(usdtRate);

    const currentKeys = Object.keys(userBalance).length;
    const previousKeys = Object.keys(completeUserBalance).length;

    if (previousKeys > 10 && currentKeys < 10 && currentKeys > 0) {
      const mergedBalance = {
        ...completeUserBalance,
        ...userBalance,
      };
      setCompleteUserBalance(mergedBalance);
      processBalanceWithData(mergedBalance);
    } else if (currentKeys > 10) {
      setCompleteUserBalance(userBalance);
      processBalance();
    } else {
      setCompleteUserBalance(userBalance);
      processBalance();
    }
  }, [
    userBalance,
    crypto_price,
    btcValue,
    USDTValue,
    processBalance,
    processBalanceWithData,
  ]);

  // ✅ Fetch trade history on initial load and when coin pair changes
  useEffect(() => {
    if (!token || !coin?.base || !coin?.quote) return;

    const newCoinPair = `${coin.base}-${coin.quote}`;

    if (
      !hasInitiallyLoaded.current ||
      currentCoinPair.current !== newCoinPair
    ) {
      fetchTradeHistory();
      hasInitiallyLoaded.current = true;
      currentCoinPair.current = newCoinPair;
    }
  }, [coin?.base, coin?.quote, token]);

  // ✅ Refetch trade history when tradeCompleted changes
  useEffect(() => {
    if (!hasInitiallyLoaded.current) return;
    fetchTradeHistory();
  }, [tradeCompleted]);

  useEffect(() => {
    if (!hasInitiallyLoaded.current || !userOpenOrders) return;
    const currentOrdersCount = userOpenOrders.length;
    if (
      previousOpenOrdersCount.current !== currentOrdersCount &&
      previousOpenOrdersCount.current !== 0
    ) {
      fetchTradeHistory();
    }

    previousOpenOrdersCount.current = currentOrdersCount;
  }, [userOpenOrders]);

  const sortedOpenOrders = useMemo(() => {
    return userOpenOrders?.sort((a, b) => {
      if (sortBy.key) {
        const valueA = a[sortBy.key];
        const valueB = b[sortBy.key];
        if (valueA < valueB) return sortBy.ascending ? -1 : 1;
        if (valueA > valueB) return sortBy.ascending ? 1 : -1;
      }
      return 0;
    });
  }, [userOpenOrders, sortBy]);

  const sortedTradeHistoryData = useMemo(() => {
    return tradeHistoryData.sort((a, b) => {
      if (sortBy.key) {
        const valueA = a[sortBy.key];
        const valueB = b[sortBy.key];
        if (valueA < valueB) return sortBy.ascending ? -1 : 1;
        if (valueA > valueB) return sortBy.ascending ? 1 : -1;
      }
      return 0;
    });
  }, [tradeHistoryData, sortBy]);

  const sortedPortfolioData = useMemo(() => {
    return portfolioData.sort((a, b) => {
      if (sortBy.key) {
        const valueA = a[sortBy.key];
        const valueB = b[sortBy.key];
        if (valueA < valueB) return sortBy.ascending ? -1 : 1;
        if (valueA > valueB) return sortBy.ascending ? 1 : -1;
      }
      return 0;
    });
  }, [portfolioData, sortBy]);

  const getSortClass = (key) => {
    if (sortBy.key !== key) return "table-sort-icon hidden";
    return sortBy.ascending ? "table-sort-icon" : "table-sort-icon rotated";
  };

  const paginationData = useMemo(() => {
    let dataSource;
    let totalItems;

    // ✅ USE TAB ID INSTEAD OF TAB NAME
    if (activeTab.id === 0) {
      dataSource = sortedOpenOrders;
      totalItems = sortedOpenOrders?.length || 0;
    } else if (activeTab.id === 1) {
      dataSource = sortedTradeHistoryData;
      totalItems = tradeHistoryData.length;
    } else {
      dataSource = [];
      totalItems = 0;
    }

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    const currentItems =
      dataSource?.slice(indexOfFirstItem, indexOfLastItem) || [];

    return {
      totalItems,
      totalPages,
      indexOfLastItem,
      indexOfFirstItem,
      currentItems,
    };
  }, [
    activeTab,
    sortedOpenOrders,
    sortedTradeHistoryData,
    currentPage,
    itemsPerPage,
    tradeHistoryData.length,
    labels,
  ]);

  let isInstitutional = false;
  if (process.env.REACT_APP_IS_INSTITUTIONAL === "true") {
    isInstitutional = true;
  }

  useEffect(() => {
    setExpandedRows({});
  }, [currentPage, activeTab]);

  // ✅ Helper to show no data row
  const renderNoDataRow = (colSpan, message) => (
    <tr>
      <td colSpan={colSpan} align="center" style={{ height: 200 }}>
        <br />
        <br />
        <span style={{ color: "#ccc" }}>
          {message || labels?.generics?.noData}
        </span>
      </td>
    </tr>
  );

  // ✅ Helper to show loading row
  const renderLoadingRow = (colSpan, message) => (
    <tr>
      <td colSpan={colSpan} align="center" style={{ height: 200 }}>
        <img src="/images/loading.gif" width={50} alt="Loading" />
        <br />
        <br />
        <span style={{ color: "#ccc" }}>
          {message || labels?.generics?.loading}
        </span>
      </td>
    </tr>
  );

  return (
    <div className="px-1 px-lg-0 px-md-0 pt-2 border-top custom-border-color border-secondary border-end">
      <ul
        className="nav nav-pills ps-0 ps-lg-3 ps-md-3"
        id="pills-tab"
        role="tablist"
      >
        {tabs.map((tab) => (
          <li className="nav-item" role="presentation" key={tab.id}>
            <button
              className={`nav-link rounded-3 py-1 px-2 px-lg-3 px-md-2 ${
                activeTab.id === tab.id ? "active" : "text-muted"
              }`}
              id={`pills-${tab?.name?.toLowerCase()}-tab`}
              data-bs-toggle="pill"
              data-bs-target={`#pills-${tab?.name?.toLowerCase()}`}
              type="button"
              role="tab"
              aria-controls={`pills-${tab?.name?.toLowerCase()}`}
              aria-selected={activeTab.id === tab.id}
              onClick={() => handleTabClick(tab)}
            >
              {tab.name}
            </button>
          </li>
        ))}
      </ul>
      <div className="tab-content" id="pills-tabContent">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`tab-pane fade ${
              activeTab.id === tab.id ? "show active" : ""
            }`}
            id={`pills-${tab?.name?.toLowerCase()}`}
            role="tabpanel"
            aria-labelledby={`pills-${tab?.name?.toLowerCase()}-tab`}
            tabIndex={0}
          >
            {tab?.name === labels?.exchange?.openOrders && (
              <div className="table-responsive table-trade-wrapper">
                <table className="table table-sm table-open-orders mb-0">
                  <thead className="w-100 border-bottom custom-border-color border-secondary pb-3">
                    <tr>
                      <th style={{ width: "20%" }}>
                        {labels?.tables?.openOrdersTable?.date}
                      </th>
                      <th style={{ width: "15%" }}>
                        {labels?.tables?.openOrdersTable?.pair}
                      </th>
                      <th style={{ width: "8.33%" }}>
                        {labels?.tables?.openOrdersTable?.type}
                      </th>
                      <th style={{ width: "8.33%" }}>
                        {labels?.tables?.openOrdersTable?.side}
                      </th>
                      <th style={{ width: "8.33%" }}>
                        {labels?.tables?.openOrdersTable?.price}
                      </th>
                      <th style={{ width: "8.33%" }}>
                        {labels?.tables?.openOrdersTable?.stopprice}
                      </th>
                      <th style={{ width: "8.33%" }}>
                        {labels?.tables?.openOrdersTable?.pending}
                      </th>
                      <th style={{ width: "8.33%" }}>
                        {labels?.tables?.openOrdersTable?.volume}
                      </th>
                      <th style={{ width: "15%" }}>
                        {labels?.tables?.openOrdersTable?.action}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {token && activeTab.id === 0 && (
                      <>
                        {paginationData.currentItems &&
                        paginationData.currentItems.length > 0
                          ? paginationData.currentItems
                              ?.filter(
                                (order, index, self) =>
                                  index ===
                                  self.findIndex(
                                    (o) => o.order_id === order.order_id
                                  )
                              )
                              .filter(
                                (order) => order.order_id !== deletedOrderId
                              )
                              .map((open, index) => {
                                const pairString =
                                  open?.base + "_" + open?.quote;
                                const actionType =
                                  open?.side.toLocaleLowerCase();
                                return (
                                  <tr key={index} className="small">
                                    <td style={{ width: "20%" }}>
                                      <div className="date ps-2 d-flex items-center gap-2">
                                        {splitDateTime(open.timestamp).date}
                                        <span className="d-block ms-0">
                                          {splitDateTime(open.timestamp).time}
                                        </span>
                                      </div>
                                    </td>
                                    <td style={{ width: "15%" }}>
                                      <div className="pair d-flex align-items-center gap-2">
                                        <img
                                          src={`/images/coins/${open?.base.toLowerCase()}.svg`}
                                          alt=""
                                          width={16}
                                          height={16}
                                        />
                                        {open?.base}/{open?.quote}
                                      </div>
                                    </td>
                                    <td style={{ width: "8.33%" }}>
                                      {open.type}
                                    </td>
                                    <td
                                      style={{ width: "8.33%" }}
                                      className={
                                        open.side === "BUY"
                                          ? "text-success"
                                          : "text-danger"
                                      }
                                    >
                                      {labels?.forms?.postOffer?.[
                                        actionType
                                      ]?.toUpperCase()}
                                    </td>
                                    <td style={{ width: "8.33%" }}>
                                      {open.rate}
                                    </td>
                                    <td style={{ width: "8.33%" }}>
                                      {open.stop_price}
                                    </td>
                                    <td style={{ width: "8.33%" }}>
                                      {open.pending}
                                    </td>
                                    <td style={{ width: "8.33%" }}>
                                      {open.volume}
                                    </td>
                                    <td style={{ width: "15%" }}>
                                      {!isInstitutional && (
                                        <button
                                          className="btn trade-btn"
                                          onClick={() =>
                                            cancelOrder(
                                              open.order_id,
                                              open.side,
                                              pairString
                                            )
                                          }
                                        >
                                          {labels?.buttons?.cancel}
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })
                          : renderNoDataRow(9, "No open orders")}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tab?.name === labels?.exchange?.orderHistory && (
              <div className="table-responsive table-trade-wrapper table-trade-history">
                <table className="table table-sm table-open-orders mb-0">
                  <thead className="w-100 border-bottom custom-border-color border-secondary pb-3">
                    <tr className="small">
                      <th style={{ width: "5%" }}></th>
                      <th style={{ width: "20%" }}>
                        {labels?.tables?.tradeHistory?.date}
                      </th>
                      <th style={{ width: "15%" }}>
                        {labels?.tables?.tradeHistory?.pair}
                      </th>
                      <th style={{ width: "10%" }}>
                        {labels?.tables?.tradeHistory?.type}
                      </th>
                      <th style={{ width: "12.5%" }}>
                        {labels?.tables?.tradeHistory?.price}
                      </th>
                      <th style={{ width: "12.5%" }}>
                        {labels?.tables?.tradeHistory?.size}
                      </th>
                      <th style={{ width: "12.5%" }}>
                        {labels?.tables?.tradeHistory?.amount}
                      </th>
                      <th style={{ width: "12.5%" }}>
                        {labels?.tables?.tradeHistory?.fee}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {token && activeTab.id === 1 && (
                      <>
                        {isLoading
                          ? renderLoadingRow(8, labels?.generics?.loading)
                          : paginationData.currentItems &&
                            paginationData.currentItems.length > 0
                          ? paginationData.currentItems?.map((trade, index) => {
                              const actionType = trade.side.toLowerCase();
                              const rowId = index;

                              return (
                                <React.Fragment key={rowId}>
                                  <tr className="small">
                                    <td style={{ width: "5%" }}>
                                      <button
                                        onClick={() => toggleRow(rowId)}
                                        className="bg-transparent border-0"
                                      >
                                        <span
                                          className={
                                            expandedRows[rowId]
                                              ? "chevron-rotated"
                                              : "chevron"
                                          }
                                        >
                                          <img src="/images/icons/chevron-down.svg" />
                                        </span>
                                      </button>
                                    </td>
                                    <td style={{ width: "20%" }}>
                                      <div className="date ps-2 d-flex items-center gap-2">
                                        {splitDateTime(trade?.date).date}
                                        <span className="d-block ms-0">
                                          {splitDateTime(trade?.date).time}
                                        </span>
                                      </div>
                                    </td>
                                    <td style={{ width: "15%" }}>
                                      <div className="pair d-flex align-items-center gap-2">
                                        <img
                                          src={`/images/coins/${trade?.trade.toLowerCase()}.svg`}
                                          width={16}
                                          height={16}
                                          alt=""
                                        />
                                        {trade?.trade}/{trade?.market}
                                      </div>
                                    </td>
                                    <td
                                      style={{ width: "10%" }}
                                      className={
                                        trade.side === "BUY"
                                          ? "text-success"
                                          : "text-danger"
                                      }
                                    >
                                      {labels?.forms?.postOffer?.[
                                        actionType
                                      ]?.toUpperCase()}
                                    </td>
                                    <td style={{ width: "12.5%" }}>
                                      {formatNumberWithComma(
                                        trade.rate,
                                        selectedNumberFormat
                                      )}{" "}
                                      {trade.market}
                                    </td>
                                    <td style={{ width: "12.5%" }}>
                                      {formatNumberWithComma(
                                        trade.volume,
                                        selectedNumberFormat
                                      )}{" "}
                                      {trade.trade}
                                    </td>
                                    <td style={{ width: "12.5%" }}>
                                      {formatNumberWithComma(
                                        trade.amount,
                                        selectedNumberFormat
                                      )}{" "}
                                      {trade.market}
                                    </td>
                                    <td style={{ width: "12.5%" }}>
                                      {formatNumberWithComma(
                                        trade.serviceCharge,
                                        selectedNumberFormat
                                      )}{" "}
                                      {trade.side === "BUY"
                                        ? trade.trade
                                        : trade.market}
                                    </td>
                                  </tr>
                                  {expandedRows[rowId] && (
                                    <tr className="table-accordion-wrapper">
                                      <td colSpan="10">
                                        <div className="accordion-content">
                                          <span>Order ID: {trade.orderId}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })
                          : renderNoDataRow(8, "No trade history")}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {tab?.name === labels?.exchange?.portfolio && (
              <div className="table-responsive table-trade-wrapper table-portfolio">
                <table className="table table-sm table-open-orders mb-0">
                  <thead className="w-100 border-bottom custom-border-color border-secondary pb-3">
                    <tr className="small">
                      <th onClick={() => handleSort("currency")}>
                        <span className="pe-2">
                          {labels?.tables?.NDWalletOverview?.currency}
                        </span>
                        <span className={getSortClass("currency")}>
                          <img
                            src="./images/icons/top.png"
                            width={16}
                            height={16}
                          />
                        </span>
                      </th>
                      <th onClick={() => handleSort("balance")}>
                        <span className="pe-2">
                          {labels?.tables?.NDWalletOverview?.balance}
                        </span>
                        <span className={getSortClass("balance")}>
                          <img
                            src="./images/icons/top.png"
                            width={16}
                            height={16}
                          />
                        </span>
                      </th>
                      <th onClick={() => handleSort("balanceInTrade")}>
                        <span className="pe-2">
                          {labels?.tables?.NDWalletOverview?.inOrders}
                        </span>
                        <span className={getSortClass("balanceInTrade")}>
                          <img
                            src="./images/icons/top.png"
                            width={16}
                            height={16}
                          />
                        </span>
                      </th>
                      <th onClick={() => handleSort("total")}>
                        <span className="pe-2">{labels?.exchange?.total}</span>
                        <span className={getSortClass("total")}>
                          <img
                            src="./images/icons/top.png"
                            width={16}
                            height={16}
                          />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {token && activeTab.id === 2 && (
                      <>
                        {sortedPortfolioData && sortedPortfolioData.length > 0
                          ? sortedPortfolioData?.map((item, index) => {
                              const formattedBalance1 =
                                item?.balanceInUsdt * selectedCurrencyRate;
                              const formattedTradeBalance =
                                item?.balanceInTradeInUsdt *
                                selectedCurrencyRate;
                              const formattedTotalBalance =
                                item?.totalInUsdt * selectedCurrencyRate;

                              return (
                                <tr key={index}>
                                  <td className="px-0 px-lg-3">
                                    <div className="pair d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-2 ps-2">
                                      <img
                                        src={`/images/coins/${item.ShortName.toLowerCase()}.svg`}
                                        width={16}
                                        height={16}
                                      />
                                      {item.LongName} / {item.ShortName}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="date">
                                      {item.balance && !hideBalances
                                        ? formatNumberWithComma(
                                            item.balance,
                                            selectedNumberFormat
                                          )
                                        : ""}{" "}
                                      {hideBalances ? "******" : ""}
                                      {!hideBalances && !item.balance
                                        ? "0"
                                        : ""}
                                      &nbsp;{item.ShortName} <br />
                                      <span className="ml0">
                                        {selectedFiatEstimation?.name}&nbsp;
                                        {item?.balanceInUsdt && !hideBalances
                                          ? formatNumberWithComma(
                                              formattedBalance1,
                                              selectedNumberFormat
                                            )
                                          : ""}
                                        {hideBalances ? "******" : ""}
                                        {!hideBalances && !item.balanceInUsdt
                                          ? "0"
                                          : ""}
                                      </span>
                                      <br />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="date">
                                      {item.balanceInTrade && !hideBalances
                                        ? formatNumberWithComma(
                                            item?.balanceInTrade,
                                            selectedNumberFormat
                                          )
                                        : ""}{" "}
                                      {hideBalances ? "******" : ""}
                                      {!hideBalances && !item.balanceInTrade
                                        ? "0"
                                        : ""}
                                      &nbsp;{item.ShortName}
                                      <br />
                                      <span className="ml0">
                                        {selectedFiatEstimation?.name}&nbsp;
                                        {item.balanceInTradeInUsdt &&
                                        !hideBalances
                                          ? formatNumberWithComma(
                                              formattedTradeBalance,
                                              selectedNumberFormat
                                            )
                                          : ""}
                                        {hideBalances ? "******" : ""}
                                        {!hideBalances &&
                                        !item.balanceInTradeInUsdt
                                          ? "0"
                                          : ""}
                                      </span>
                                      <br />
                                    </div>
                                  </td>
                                  <td>
                                    <div className="date">
                                      {item.total && !hideBalances
                                        ? formatNumberWithComma(
                                            item?.total,
                                            selectedNumberFormat
                                          )
                                        : ""}{" "}
                                      {hideBalances ? "******" : ""}
                                      {!hideBalances && !item.total ? "0" : ""}
                                      &nbsp;{item.ShortName}
                                      <br />
                                      <span className="ml0">
                                        {selectedFiatEstimation?.name}&nbsp;
                                        {item.totalInUsdt && !hideBalances
                                          ? formatNumberWithComma(
                                              formattedTotalBalance,
                                              selectedNumberFormat
                                            )
                                          : ""}
                                        {hideBalances ? "******" : ""}
                                        {!hideBalances && !item.totalInUsdt
                                          ? "0"
                                          : ""}
                                      </span>
                                      <br />
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          : renderNoDataRow(4, "No balance data available")}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            <ul className="pagination pagination-container">
              {((activeTab.id === 0 && userOpenOrders?.length > 10) ||
                (activeTab.id === 1 && tradeHistoryData.length > 10)) && (
                <>
                  <li>
                    {currentPage > 1 ? (
                      <button
                        className="btn btn-pagination"
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        {labels?.pagination?.prev}
                      </button>
                    ) : (
                      <button className="btn btn-pagination disabled" disabled>
                        {labels?.pagination?.prev}
                      </button>
                    )}
                  </li>
                  <li>
                    {activeTab.id === 0 &&
                    paginationData.indexOfLastItem < userOpenOrders?.length ? (
                      <button
                        className="btn btn-pagination"
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        {labels?.pagination?.next}
                      </button>
                    ) : activeTab.id === 1 &&
                      paginationData.indexOfLastItem <
                        tradeHistoryData.length ? (
                      <button
                        className="btn btn-pagination"
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        {labels?.pagination?.next}
                      </button>
                    ) : (
                      <button className="btn btn-pagination disabled" disabled>
                        {labels?.pagination?.next}
                      </button>
                    )}
                  </li>
                </>
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};
